// Live ledger — reads pons' holder-fee-sharing distributor straight from the chain in the
// browser (public RPC, CORS *). Two reads:
//   fetchLedger()      epochs + pending, same shape as data/dividends.json (bundled fallback)
//   fetchPaidTo(addr)  every MSFT airdrop from the distributor to ONE wallet (Transfer logs)
// Chain-derived only. If the RPC is down we show the last good reading, never a made-up number.
import { useEffect, useState } from 'react';
import bundled from 'data/dividends.json';
import { compute } from 'data/epochs';
import { MSFT, RPC } from 'data/msft';

export const DISTRIBUTOR = bundled.distributor || '0xF346218BFC30564efe39988e6a14188525297d98';
export const LAUNCH_BLOCK = 55282101; // launch tx block, 2026-09-05
const TRANSFER = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
const SEL = {
  epochCount: '0x829965cc',
  unallocatedQuote: '0x2a29f60e',
  claimWindow: '0x9f34fc80',
  getEpoch: '0xbc0bc6ba',
};
const KEY = 'ic:ledger:v1';
const REFRESH_MS = 60000;

let rpcId = 0;
async function rpc(calls) {
  // calls: [{method, params}] → results in order (JSON-RPC batch)
  const body = calls.map(c => ({ jsonrpc: '2.0', id: ++rpcId, method: c.method, params: c.params }));
  const r = await fetch(RPC, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body.length === 1 ? body[0] : body),
  });
  if (!r.ok) throw new Error(`rpc ${r.status}`);
  const j = await r.json();
  const arr = Array.isArray(j) ? j : [j];
  const byId = new Map(arr.map(x => [x.id, x]));
  return body.map(b => {
    const x = byId.get(b.id);
    if (!x || x.error) throw new Error((x && x.error && x.error.message) || 'rpc error');
    return x.result;
  });
}
const call = (to, data) => ({ method: 'eth_call', params: [{ to, data }, 'latest'] });
const pad = a => `0x${'0'.repeat(24)}${a.toLowerCase().replace(/^0x/, '')}`;
const word = (hex, i) => BigInt(`0x${hex.slice(2 + i * 64, 2 + (i + 1) * 64)}`);
const hexNum = n => `0x${n.toString(16)}`;

export async function fetchLedger() {
  const [countHex, pendingHex, windowHex] = await rpc([
    call(DISTRIBUTOR, SEL.epochCount),
    call(DISTRIBUTOR, SEL.unallocatedQuote),
    call(DISTRIBUTOR, SEL.claimWindow),
  ]);
  const count = Number(BigInt(countHex));
  const claimWindow = BigInt(windowHex);
  const epochs = [];
  if (count > 0) {
    const raws = await rpc(
      Array.from({ length: count }, (_, i) => call(DISTRIBUTOR, SEL.getEpoch + i.toString(16).padStart(64, '0'))),
    );
    raws.forEach((raw, i) => {
      const root = `0x${raw.slice(2, 66)}`;
      const quoteTotal = word(raw, 1);
      const coinTotal = word(raw, 2);
      const quoteClaimed = word(raw, 3);
      const expiresAt = word(raw, 5);
      const rolledOver = word(raw, 6) !== BigInt(0);
      const publishedAt = expiresAt > claimWindow ? new Date(Number(expiresAt - claimWindow) * 1000).toISOString() : null;
      epochs.push({
        id: i,
        publishedAt,
        expiresAt: Number(expiresAt),
        total: quoteTotal.toString(),
        claimed: quoteClaimed.toString(),
        coinTotal: coinTotal.toString(),
        rolledOver,
        root,
      });
    });
  }
  const out = {
    asset: bundled.asset || 'MSFT',
    decimals: bundled.decimals || 18,
    distributor: DISTRIBUTOR,
    updatedAt: new Date().toISOString(),
    pending: BigInt(pendingHex).toString(),
    epochs,
    live: true,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch (e) {
    /* private mode */
  }
  return out;
}

function cached() {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || 'null');
    return c && Array.isArray(c.epochs) ? { ...c, live: false } : null;
  } catch (e) {
    return null;
  }
}

let inflight = null;
let last = null;
let lastAt = 0;
export function getLedger(force) {
  if (!force && last && Date.now() - lastAt < REFRESH_MS) return Promise.resolve(last);
  if (inflight) return inflight;
  inflight = fetchLedger()
    .then(l => {
      last = l;
      lastAt = Date.now();
      return l;
    })
    .catch(() => last || cached() || { ...bundled, live: false })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

// React hook: bundled numbers instantly, live numbers seconds later, refreshed every minute.
export function useLedger() {
  const [data, setData] = useState(() => last || cached() || { ...bundled, live: false });
  useEffect(() => {
    let alive = true;
    const load = () => getLedger().then(l => alive && setData(l));
    load();
    const t = setInterval(() => getLedger(true).then(l => alive && setData(l)), REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  return { ...compute(data), live: !!data.live };
}

// ---- per-wallet payouts: MSFT Transfer(distributor → wallet) logs since launch ----
export const isAddress = a => /^0x[0-9a-fA-F]{40}$/.test(String(a || '').trim());
const paidCache = new Map();

export async function fetchPaidTo(address) {
  const addr = String(address || '').trim();
  if (!isAddress(addr)) throw new Error('not an address');
  const k = addr.toLowerCase();
  const hit = paidCache.get(k);
  if (hit && Date.now() - hit.at < REFRESH_MS) return hit;
  const filter = { address: MSFT, topics: [TRANSFER, pad(DISTRIBUTOR), pad(addr)], fromBlock: hexNum(LAUNCH_BLOCK), toBlock: 'latest' };
  let logs;
  try {
    [logs] = await rpc([{ method: 'eth_getLogs', params: [filter] }]);
  } catch (e) {
    // some RPCs cap the block range — walk it in 200k-block chunks
    const [latestHex] = await rpc([{ method: 'eth_blockNumber', params: [] }]);
    const latest = Number(BigInt(latestHex));
    logs = [];
    for (let from = LAUNCH_BLOCK; from <= latest; from += 200000) {
      const to = Math.min(latest, from + 199999);
      const [part] = await rpc([{ method: 'eth_getLogs', params: [{ ...filter, fromBlock: hexNum(from), toBlock: hexNum(to) }] }]);
      logs = logs.concat(part);
    }
  }
  const DEC = BigInt('1000000000000000000');
  const receipts = logs.map(l => ({
    tx: l.transactionHash,
    block: Number(BigInt(l.blockNumber)),
    raw: BigInt(l.data).toString(),
    amount: Number((BigInt(l.data) * BigInt(1000000)) / DEC) / 1000000,
  }));
  const total = receipts.reduce((s, r) => s + r.amount, 0);
  const out = { address: addr, total, count: receipts.length, receipts, at: Date.now() };
  paidCache.set(k, out);
  return out;
}

// Ask an injected wallet (MetaMask, Rabby…) for its address — optional, paste works too.
export async function connectWallet() {
  const eth = typeof window !== 'undefined' && window.ethereum;
  if (!eth || !eth.request) throw new Error('no wallet');
  const accounts = await eth.request({ method: 'eth_requestAccounts' });
  return accounts && accounts[0];
}
